import { translate, type Lang } from './i18n'
import { OBJECTIVE, compareKpis, humanize, policyDifferences } from './compare'
import type { Scenario } from './api'

/**
 * Dos escenarios, lado a lado (§9.4, paso «Comparar»).
 *
 * Primero **qué cambia** y después **qué resultó**, en ese orden, porque la pregunta del planner
 * no es cuál gana sino por qué: un escenario con más cobertura no dice nada si no se sabe qué
 * perilla se movió para conseguirla.
 */
export function ScenarioComparison({ lang, a, b }: { lang: Lang; a: Scenario; b: Scenario }) {
  const cambios = policyDifferences(a.policy, b.policy)
  const filas = compareKpis(a.kpis, b.kpis)

  // Un interruptor del optimizador llega como lo escribe .NET —«True», «False»— y así se veía en
  // la tabla. En una comparación de perillas, «on» y «off» es lo que quien planifica busca.
  const valor = (x: string) =>
    x.toLowerCase() === 'true' ? translate(lang, 'planner.on')
      : x.toLowerCase() === 'false' ? translate(lang, 'planner.off')
        : x

  // La regla de división con su nombre, no el del enum («Strict», «Prefer»).
  const regla = (x: string) => {
    const t = translate(lang, `planner.rule.${x.toLowerCase()}`)
    return t.startsWith('planner.rule.') ? x : t
  }

  // Los porcentajes y las razones se leen distinto: dos decimales sobran en una cobertura y
  // faltan en una fracción de reserva.
  const numero = (x: number) => (Number.isInteger(x) ? String(x) : x.toFixed(x < 10 ? 2 : 1))
  // Las coberturas y el uso, en porcentaje como en la tarjeta del escenario: el uso llega como
  // fracción y la tabla decía «0.09» junto a una tarjeta que decía «8.5 %».
  const kpi = (key: string, x: number) =>
    key === 'utilization' ? `${(100 * x).toFixed(1)} %`
      : key === 'coverage' || key === 'riskWeightedCoverage' ? `${x.toFixed(1)} %`
        : numero(x)

  return (
    <div className="card">
      <table className="compare">
        <caption>{translate(lang, 'compare.changed')}</caption>
        <tbody>
          {cambios.length === 0 && (
            <tr><td colSpan={3}>{translate(lang, 'compare.same')}</td></tr>
          )}
          {cambios.map((c) => {
            const etiqueta = translate(lang, `knob.${c.key}`)
            return (
              <tr key={c.key}>
                <td>{etiqueta === `knob.${c.key}` ? humanize(c.key) : etiqueta}</td>
                <td className="num">{c.key === 'divisionRule' ? regla(c.a) : valor(c.a)}</td>
                <td className="num">{c.key === 'divisionRule' ? regla(c.b) : valor(c.b)}</td>
              </tr>
            )
          })}
        </tbody>
      </table>

      <table className="compare">
        <caption>{translate(lang, 'compare.result')}</caption>
        <tbody>
          {filas.map((f) => (
            <tr key={f.key} className={f.key === OBJECTIVE ? 'objective' : ''}>
              <td>
                {translate(lang, `kpi.${f.key}`)}
                {f.key === OBJECTIVE &&
                  <span className="meta"> · {translate(lang, 'compare.objective')}</span>}
              </td>
              <td className={`num ${f.better === 'a' ? 'win' : ''}`}>{kpi(f.key, f.a)}</td>
              <td className={`num ${f.better === 'b' ? 'win' : ''}`}>{kpi(f.key, f.b)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
